import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, Check, PencilLine, Plus, RefreshCcw, X } from 'lucide-react';
import TensionEditor, { OperationCard } from './TensionEditor';
import ProposalDiff from './ProposalDiff';
import SharedNotes from './SharedNotes';
import {
  StatusChip,
  buildOperation,
  describeOperation,
  findCircle,
  formatDate,
  operationIssue,
  parseOperations,
  sameId,
  useStableKeys,
} from './governance';

const isDecidedStatus = (status) => {
  const normalized = String(status || '').toLowerCase();
  return normalized === 'applied' || normalized.startsWith('reject');
};

// Empreinte d'une décision : change quand le serveur a traité la décision (statut ou erreur). Pas `updated_at` :
// la dernière saisie, envoyée juste avant la décision, le change aussi.
const decisionStamp = (tension) => (tension ? `${tension.status}|${tension.decision_error || ''}` : 'gone');
const DECISION_TIMEOUT_MS = 10000;

// Ancien champ proposed_data, déduit du premier changement (gardé à jour pour les vues historiques).
const proposedFromOperation = (op, tension) => {
  if (!op) return tension?.proposed_data || {};
  if ((op.action || 'edit') === 'delete') return { delete: true };
  const data = op.data || {};
  const proposed = {};
  ['name', 'purpose', 'domains', 'accountabilities', 'reserved_domains'].forEach((key) => {
    if (data[key] !== undefined && data[key] !== null) proposed[key] = data[key];
  });
  if (op.action === 'create') proposed.parent_id = data.parent_id ?? op.circle_id ?? null;
  return proposed;
};

const card = 'bg-white rounded-xl border border-slate-200 shadow-sm';
const sectionTitle = 'text-xs font-bold text-slate-500 uppercase tracking-wider';

/**
 * Réunion de gouvernance. Le secrétaire choisit la tension montrée à tous ; chacun lit la même proposition, en clair
 * (avant / après, rôle par rôle). Le secrétaire l'amende en direct (« Amender ») puis la fait accepter ou rejeter.
 */
const GovernancePanel = ({
  selectedCircle,
  activeMeeting,
  meetingError,
  onDismissError,
  meetingParticipants = [],
  meetingDraft,
  meetingTensions,
  selectedMeetingTensionId,
  meetingNotes,
  presenceCount,
  connectedUsers = [],
  realtimeConnected,
  meetingFormVisible = false,
  meetingPoints = [],
  selectedMeetingPointId,
  tensions,
  circles,
  employees = [],
  user,
  meetingLoading,
  onCloseMeeting,
  onImportTension,
  onTransferSecretary,
  onInviteParticipant,
  onMeetingDraftChange,
  onCreateMeetingTension,
  onRefreshMeetingTensions,
  onSelectMeetingTension,
  onLiveEditTension,
  onMeetingDecision,
  onStructureChanged,
  onAddMeetingPoint,
  onSelectMeetingPoint,
  onPointDecision,
  onPointToTension,
  onNotesChange,
  onToggleForm,
}) => {
  const effectiveUserId = user?.employee_id ?? user?.id;
  const userIsSecretary = String(activeMeeting?.created_by) === String(effectiveUserId);
  const meetingCircleId = activeMeeting?.circle_id || selectedCircle?.id;
  const secretary = meetingParticipants.find((p) => String(p.id) === String(activeMeeting?.created_by));
  const [newPointTitle, setNewPointTitle] = useState('');
  const [inviteEmployeeId, setInviteEmployeeId] = useState('');
  const [confirmDecision, setConfirmDecision] = useState(null); // 'accept' | 'reject'
  const [pendingDecision, setPendingDecision] = useState(null); // { id, stamp, at }
  const [amending, setAmending] = useState(false);
  const allMeetingTensions = useMemo(() => meetingTensions || [], [meetingTensions]);

  const meetingParticipantIds = useMemo(
    () => new Set((meetingParticipants || []).map((participant) => String(participant.id))),
    [meetingParticipants]
  );
  const isParticipantConnected = useCallback(
    (participantId) => connectedUsers.some((u) => {
      const connectedId = u?.employee_id ?? u?.user_id ?? u?.id;
      return String(connectedId) === String(participantId);
    }),
    [connectedUsers]
  );
  const availableInvitees = useMemo(
    () => (employees || [])
      .filter((emp) => emp && emp.is_active !== false)
      .filter((emp) => !meetingParticipantIds.has(String(emp.id))),
    [employees, meetingParticipantIds]
  );
  const meetingScopeCircles = useMemo(() => {
    if (!meetingCircleId) return [];
    return (circles || []).filter((circle) =>
      String(circle.id) === String(meetingCircleId) || String(circle.parent_id) === String(meetingCircleId)
    );
  }, [circles, meetingCircleId]);
  const assignedMeetingRoles = useMemo(() => {
    if (!meetingScopeCircles.length || !effectiveUserId) return [];
    const userId = String(effectiveUserId);
    return meetingScopeCircles.filter((circle) => {
      if (circle.owner_id !== undefined && String(circle.owner_id) === userId) return true;
      return Array.isArray(circle.owner_ids) && circle.owner_ids.some((id) => String(id) === userId);
    });
  }, [meetingScopeCircles, effectiveUserId]);
  const expressedRoleOptions = userIsSecretary ? meetingScopeCircles : assignedMeetingRoles;

  // Tensions importables : ses propres brouillons du cercle de la réunion (le serveur ne renvoie que les siens).
  const importableTensions = useMemo(() => (tensions || []).filter((t) => {
    if (!meetingCircleId || String(t.circle_id) !== String(meetingCircleId)) return false;
    return (t.status || 'draft').toLowerCase() === 'draft';
  }), [tensions, meetingCircleId]);

  const pastMeetingTensions = allMeetingTensions.filter((t) => isDecidedStatus(t.status));
  const activeMeetingTensions = allMeetingTensions.filter((t) => !isDecidedStatus(t.status));
  const selectedTension = allMeetingTensions.find((t) => sameId(t.id, selectedMeetingTensionId)) || null;
  const selectedIsDecided = Boolean(selectedTension && isDecidedStatus(selectedTension.status));
  const selectedOps = useMemo(() => parseOperations(selectedTension?.operations), [selectedTension]);
  const selectedOpsRef = useRef(selectedOps);
  selectedOpsRef.current = selectedOps;
  const opKeys = useStableKeys(selectedOps.length);
  const decisionInFlight = Boolean(pendingDecision);
  const canEdit = userIsSecretary && !selectedIsDecided;
  // Une proposition encore vide s'ouvre directement en édition chez le secrétaire.
  const editing = canEdit && (amending || selectedOps.length === 0);
  const nextTension = activeMeetingTensions.find((t) => !sameId(t.id, selectedTension?.id)) || null;

  // Une décision en cours se termine quand le serveur renvoie la tension décidée (ou au bout de 10 s, comptées
  // depuis le clic : les rafraîchissements ne relancent pas le délai).
  useEffect(() => {
    if (!pendingDecision) return undefined;
    const current = allMeetingTensions.find((t) => sameId(t.id, pendingDecision.id));
    if (decisionStamp(current) !== pendingDecision.stamp) {
      setPendingDecision(null);
      return undefined;
    }
    const timer = setTimeout(() => setPendingDecision(null), Math.max(0, pendingDecision.at + DECISION_TIMEOUT_MS - Date.now()));
    return () => clearTimeout(timer);
  }, [pendingDecision, allMeetingTensions]);
  useEffect(() => {
    if (meetingError) setPendingDecision(null);
  }, [meetingError]);
  useEffect(() => {
    setConfirmDecision(null);
    setAmending(false);
  }, [selectedMeetingTensionId]);

  // Une tension vient d'être appliquée : la structure a changé, on la recharge.
  const appliedIdsRef = useRef(null);
  useEffect(() => {
    const applied = new Set(
      allMeetingTensions.filter((t) => String(t.status || '').toLowerCase() === 'applied').map((t) => String(t.id))
    );
    const previous = appliedIdsRef.current;
    appliedIdsRef.current = applied;
    if (previous && onStructureChanged && [...applied].some((id) => !previous.has(id))) onStructureChanged();
  }, [allMeetingTensions, onStructureChanged]);

  const pushLiveEdit = (tension, patch) => {
    if (!tension || !onLiveEditTension || !canEdit) return;
    const operations = patch.operations ?? selectedOpsRef.current;
    selectedOpsRef.current = operations;
    onLiveEditTension(tension.id, {
      title: patch.title ?? tension.title,
      summary: patch.summary ?? tension.summary,
      action: operations[0]?.action || tension.action,
      proposed_data: proposedFromOperation(operations[0], tension),
      operations,
      expressed_by_role_id: tension.expressed_by_role_id,
    });
  };
  const tensionCircleId = selectedTension?.circle_id ?? meetingCircleId ?? null;
  const handleUpdateOperation = (idx, nextOp) => {
    pushLiveEdit(selectedTension, {
      operations: selectedOpsRef.current.map((op, i) => (i === idx ? { ...nextOp, circle_id: tensionCircleId } : op)),
    });
  };
  const handleRemoveOperation = (idx) => {
    opKeys.remove(idx);
    pushLiveEdit(selectedTension, { operations: selectedOpsRef.current.filter((_, i) => i !== idx) });
  };
  const handleAddOperation = () => {
    opKeys.append();
    setAmending(true);
    pushLiveEdit(selectedTension, { operations: [...selectedOpsRef.current, buildOperation('edit', tensionCircleId)] });
  };

  const decide = (decision) => {
    if (!selectedTension || decisionInFlight || !onMeetingDecision) return;
    setPendingDecision({ id: selectedTension.id, stamp: decisionStamp(selectedTension), at: Date.now() });
    setConfirmDecision(null);
    setAmending(false);
    onMeetingDecision(selectedTension.id, decision);
  };

  const updateMeetingDraftField = useCallback((key, value) => {
    if (!onMeetingDraftChange) return;
    onMeetingDraftChange((prev) => ({ ...(prev || {}), [key]: value }));
  }, [onMeetingDraftChange]);

  const selectedPoint = meetingPoints.find((point) => String(point.id) === String(selectedMeetingPointId)) || null;
  const visibleMeetingPoints = useMemo(
    () => meetingPoints.filter((point) => !['treated', 'closed'].includes((point.status || '').toLowerCase())),
    [meetingPoints]
  );
  const handleAddPoint = () => {
    const trimmed = (newPointTitle || '').trim();
    if (!trimmed) return;
    if (onAddMeetingPoint) onAddMeetingPoint(trimmed);
    setNewPointTitle('');
  };
  const pointStatusLabel = (status) => {
    const norm = (status || 'open').toLowerCase();
    if (norm === 'closed') return 'Clôturé';
    if (norm === 'treated') return 'Traité';
    if (norm === 'converted') return 'Transformé en tension';
    return 'Ouvert';
  };

  const opIssues = selectedOps.map(operationIssue);
  const acceptBlocker = selectedOps.length === 0
    ? 'Aucun changement proposé : ajoute-en un ou rejette la tension.'
    : opIssues.find(Boolean) || null;
  const expressedBy = findCircle(circles, selectedTension?.expressed_by_role_id);
  const circleName = selectedCircle?.name || findCircle(circles, meetingCircleId)?.name || 'Réunion';

  const renderTensionItem = (tension) => {
    const selected = sameId(selectedMeetingTensionId, tension.id);
    const ops = parseOperations(tension.operations);
    return (
      <button
        key={tension.id}
        type="button"
        onClick={() => onSelectMeetingTension && onSelectMeetingTension(tension.id)}
        disabled={!userIsSecretary}
        title={userIsSecretary ? 'Montrer cette tension à tous' : undefined}
        className={`w-full text-left p-3 rounded-xl border transition-all duration-200 ${selected ? 'bg-indigo-50 border-indigo-300 shadow-sm ring-1 ring-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-200 hover:shadow-sm'} ${userIsSecretary ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex justify-between items-start gap-2">
          <span className={`text-sm font-semibold min-w-0 ${selected ? 'text-indigo-900' : 'text-slate-800'}`}>
            {tension.title || 'Sans titre'}
          </span>
          <StatusChip tension={tension} className="shrink-0" />
        </div>
        <p className="mt-0.5 text-[11px] text-slate-500">
          {tension.author_name || 'Un membre'}
          {String(tension.author_id) === String(effectiveUserId) ? ' (moi)' : ''}
        </p>
        {ops.length > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {ops.slice(0, 3).map((op, idx) => (
              <li key={`${op.action || 'edit'}-${op.target_circle_id ?? 'new'}-${idx}`} className="text-[11px] leading-snug text-slate-600">
                {describeOperation(op, circles)}
              </li>
            ))}
            {ops.length > 3 && <li className="text-[11px] text-slate-400">+ {ops.length - 3} autre{ops.length - 3 > 1 ? 's' : ''}</li>}
          </ul>
        )}
      </button>
    );
  };

  const draftOps = parseOperations(meetingDraft?.operations);
  const newTensionForm = (
    <div className={`${card} p-5 space-y-4`}>
      <div className="flex justify-between items-start gap-3">
        <div>
          <p className={sectionTitle}>Nouvelle tension en séance</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {userIsSecretary
              ? 'Ajoutée à la réunion, elle s\'affiche aussitôt pour tous.'
              : `${secretary?.name || 'Le secrétaire'} la rédige ; elle s'affiche ici en direct.`}
          </p>
        </div>
        {userIsSecretary && (
          <button
            type="button"
            onClick={() => onToggleForm && onToggleForm(false)}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Fermer le formulaire"
          >
            <X size={18} />
          </button>
        )}
      </div>
      {userIsSecretary ? (
        <TensionEditor
          tensionDraft={meetingDraft}
          circles={meetingScopeCircles}
          user={user}
          tensionVotes={[]}
          tensionVotesLoading={false}
          tensionsLoading={meetingLoading}
          onUpdateField={updateMeetingDraftField}
          onDelete={() => { }}
          onPropose={() => { }}
          onWithdraw={() => { }}
          onCancel={() => onToggleForm && onToggleForm(false)}
          onSave={() => onCreateMeetingTension && onCreateMeetingTension()}
          mode="meeting"
          expressedRoleOptions={expressedRoleOptions}
          defaultCircleId={meetingCircleId}
        />
      ) : (
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-slate-900">{meetingDraft?.title || 'Sans titre pour l\'instant'}</h3>
          {meetingDraft?.summary && <p className="text-sm text-slate-700 whitespace-pre-line">{meetingDraft.summary}</p>}
          {draftOps.length > 0 ? (
            <ProposalDiff operations={draftOps} circles={circles} heading={null} readable />
          ) : (
            <p className="text-sm italic text-slate-400">Pas encore de changement proposé.</p>
          )}
        </div>
      )}
    </div>
  );

  const tensionStage = selectedTension ? (
    <div className="space-y-4">
      <div className={`${card} p-5 space-y-3`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {editing ? (
              <input
                value={selectedTension.title || ''}
                onChange={(e) => pushLiveEdit(selectedTension, { title: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-lg font-bold text-slate-900 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-50"
                placeholder="Titre de la tension…"
                aria-label="Titre de la tension"
              />
            ) : (
              <h2 className="text-2xl font-bold text-slate-900 break-words">{selectedTension.title || 'Sans titre'}</h2>
            )}
            <p className="mt-1 text-xs text-slate-500">
              Proposée par {selectedTension.author_name || 'un membre'}
              {expressedBy ? ` · ressentie depuis le rôle « ${expressedBy.name} »` : ''}
            </p>
          </div>
          <StatusChip tension={selectedTension} className="shrink-0 mt-1" />
        </div>
        {editing ? (
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase text-slate-500">Ce qui coince</span>
            <textarea
              value={selectedTension.summary || ''}
              onChange={(e) => pushLiveEdit(selectedTension, { summary: e.target.value })}
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-50"
              placeholder="La situation qui gêne…"
            />
          </label>
        ) : selectedTension.summary ? (
          <div className="rounded-lg bg-amber-50/60 border border-amber-100 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase text-amber-700">Ce qui coince</p>
            <p className="text-sm text-slate-800 whitespace-pre-line">{selectedTension.summary}</p>
          </div>
        ) : null}
      </div>

      {selectedIsDecided && (
        <div className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm ${String(selectedTension.status).toLowerCase() === 'applied' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          <span>
            {String(selectedTension.status).toLowerCase() === 'applied'
              ? 'Acceptée : les changements ci-dessous sont appliqués, la structure est à jour.'
              : `Rejetée${selectedTension.rejected_at ? ` le ${formatDate(selectedTension.rejected_at)}` : ''} : rien n'a changé dans la structure. L'auteur peut la retravailler.`}
          </span>
          {userIsSecretary && nextTension && (
            <button
              type="button"
              onClick={() => onSelectMeetingTension && onSelectMeetingTension(nextTension.id)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 border border-indigo-200 hover:bg-indigo-50"
            >
              Tension suivante : {nextTension.title || 'Sans titre'} <ArrowRight size={13} />
            </button>
          )}
        </div>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className={sectionTitle}>
            {editing ? `Proposition en cours d'amendement (${selectedOps.length})` : `Proposition (${selectedOps.length} changement${selectedOps.length > 1 ? 's' : ''})`}
          </h3>
          {canEdit && (
            <div className="flex items-center gap-2">
              {editing && (
                <button
                  type="button"
                  onClick={handleAddOperation}
                  disabled={decisionInFlight}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                >
                  <Plus size={14} /> Ajouter un changement
                </button>
              )}
              {selectedOps.length > 0 && (
                <button
                  type="button"
                  onClick={() => setAmending((value) => !value)}
                  disabled={decisionInFlight}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50 ${editing ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'}`}
                >
                  {editing ? <><Check size={14} /> Terminer l'amendement</> : <><PencilLine size={14} /> Amender</>}
                </button>
              )}
            </div>
          )}
        </div>
        {editing ? (
          selectedOps.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
              Aucun changement proposé pour l'instant.{' '}
              <button type="button" onClick={handleAddOperation} className="font-semibold text-indigo-600 hover:text-indigo-800">
                Ajouter un changement
              </button>
            </div>
          ) : (
            selectedOps.map((op, idx) => (
              <OperationCard
                key={opKeys.keys[idx]}
                op={{ ...op, circle_id: tensionCircleId }}
                idx={idx}
                total={selectedOps.length}
                circles={circles}
                disabled={decisionInFlight}
                onUpdate={handleUpdateOperation}
                onRemove={handleRemoveOperation}
                issue={opIssues[idx]}
              />
            ))
          )
        ) : (
          <ProposalDiff
            operations={selectedOps}
            circles={circles}
            original={selectedTension.original_data}
            proposed={selectedTension.proposed_data}
            action={selectedTension.action}
            circleLabel={selectedTension.original_data?.name}
            heading={null}
            readable
          />
        )}
      </div>
    </div>
  ) : (
    <div className={`${card} min-h-[260px] flex flex-col items-center justify-center text-slate-400 text-center p-6`}>
      <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
        <ArrowRight size={22} className="text-slate-300" />
      </div>
      <p className="text-sm">
        {activeMeetingTensions.length === 0
          ? 'Aucune tension à traiter : importez-en une ou créez-la en séance.'
          : userIsSecretary ? 'Choisis la tension à traiter dans la liste : elle s\'affiche pour tous.' : 'Le secrétaire va choisir une tension.'}
      </p>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* En-tête */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">
            <RefreshCcw size={12} /> Réunion de gouvernance
          </div>
          <h2 className="text-2xl font-bold text-slate-900">{circleName}</h2>
          <p className="text-xs text-slate-500 mt-1">
            Secrétaire : {secretary?.name || 'Non défini'}
            <span className="text-slate-400"> · pour chaque tension : présenter, clarifier, réagir, amender, objections, décider</span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${realtimeConnected ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
            <div className={`w-2 h-2 rounded-full ${realtimeConnected ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
            {realtimeConnected ? 'En direct' : 'Reconnexion…'}
            {presenceCount > 0 && <span className="ml-1 opacity-75">({presenceCount})</span>}
          </div>

          {userIsSecretary && (
            <div className="flex gap-2">
              {!meetingFormVisible && (
                <button
                  type="button"
                  onClick={() => onToggleForm && onToggleForm(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  <Plus size={14} /> Nouvelle tension
                </button>
              )}
              <button
                type="button"
                onClick={onCloseMeeting}
                className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors"
              >
                Clôturer
              </button>
            </div>
          )}
        </div>
      </div>

      {meetingError && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
          <AlertCircle size={16} className="shrink-0" />
          <span className="flex-1">{meetingError}</span>
          {onDismissError && (
            <button type="button" onClick={onDismissError} className="text-red-400 hover:text-red-700" aria-label="Fermer le message">
              <X size={14} />
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Gauche : ordre du jour (tensions à traiter, décidées) */}
        <div className="col-span-12 lg:col-span-3 flex flex-col gap-4">
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className={sectionTitle}>À traiter ({activeMeetingTensions.length})</h3>
              <button
                type="button"
                onClick={() => activeMeeting && onRefreshMeetingTensions && onRefreshMeetingTensions(activeMeeting.id)}
                className="text-slate-400 hover:text-indigo-600 transition-colors"
                title="Actualiser"
                aria-label="Actualiser les tensions"
              >
                <RefreshCcw size={14} />
              </button>
            </div>
            {activeMeetingTensions.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-2">Aucune tension à traiter.</p>
            ) : (
              <div className="space-y-2">{activeMeetingTensions.map(renderTensionItem)}</div>
            )}
            {!userIsSecretary && activeMeetingTensions.length > 0 && (
              <p className="text-[11px] text-slate-400">Le secrétaire choisit la tension affichée pour tous.</p>
            )}
            <label className="block space-y-1 pt-2 border-t border-slate-200">
              <span className="text-[11px] font-semibold text-slate-500">Importer un de mes brouillons</span>
              <select
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && onImportTension) onImportTension(Number(val));
                }}
                disabled={importableTensions.length === 0}
              >
                <option value="">
                  {importableTensions.length === 0 ? 'Aucun brouillon à importer' : 'Choisir un brouillon'}
                </option>
                {importableTensions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title || t.original_data?.name || 'Tension'}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {pastMeetingTensions.length > 0 && (
            <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-2">
              <h3 className={sectionTitle}>Décidées ({pastMeetingTensions.length})</h3>
              <div className="space-y-2 max-h-72 overflow-y-auto">{pastMeetingTensions.map(renderTensionItem)}</div>
            </div>
          )}
        </div>

        {/* Centre : la tension montrée à tous (ou la nouvelle tension en cours de rédaction) */}
        <div className="col-span-12 lg:col-span-6 space-y-4">
          {meetingFormVisible ? newTensionForm : tensionStage}
        </div>

        {/* Droite : décision, participants, notes, points */}
        <div className="col-span-12 lg:col-span-3 flex flex-col gap-4">
          {selectedTension && !selectedIsDecided && !meetingFormVisible && (
            <div className="bg-white rounded-xl p-4 border border-indigo-100 shadow-sm space-y-3 lg:sticky lg:top-4">
              <h3 className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Décision</h3>
              {selectedTension.decision_error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>Non appliquée : {selectedTension.decision_error}</span>
                </div>
              )}
              <div className="text-xs text-slate-600 space-y-1">
                <p className="font-semibold text-slate-700">Si vous acceptez :</p>
                {selectedOps.length === 0 ? (
                  <p className="italic text-slate-400">rien ne change.</p>
                ) : (
                  <ul className="list-disc pl-4 space-y-0.5">
                    {selectedOps.map((op, idx) => (
                      <li key={opKeys.keys[idx]}>{describeOperation(op, circles)}</li>
                    ))}
                  </ul>
                )}
              </div>
              {userIsSecretary ? (
                confirmDecision ? (
                  <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs text-slate-700">
                      {confirmDecision === 'accept'
                        ? 'Confirmer : ces changements sont appliqués à la structure, tous ensemble.'
                        : "Confirmer le rejet : rien ne change, la tension est marquée refusée et son auteur pourra la retravailler."}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmDecision(null)}
                        className="flex-1 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(confirmDecision)}
                        disabled={decisionInFlight}
                        className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 ${confirmDecision === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
                      >
                        <Check size={13} /> {confirmDecision === 'accept' ? 'Appliquer' : 'Rejeter'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {acceptBlocker && <p className="text-[11px] text-amber-700">À compléter avant d'accepter : {acceptBlocker}</p>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmDecision('reject')}
                        disabled={decisionInFlight}
                        className="flex-1 px-3 py-2 text-sm font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      >
                        Rejeter
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDecision('accept')}
                        disabled={decisionInFlight || Boolean(acceptBlocker)}
                        className="flex-1 px-3 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
                      >
                        Accepter
                      </button>
                    </div>
                    {decisionInFlight && <p className="text-[11px] text-slate-500">Décision en cours…</p>}
                  </div>
                )
              ) : (
                <p className="text-[11px] text-slate-500">
                  Le secrétaire ({secretary?.name || 'non défini'}) prend la décision avec le cercle.
                </p>
              )}
            </div>
          )}

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <h3 className={`${sectionTitle} mb-3`}>Participants</h3>
            <div className="flex flex-wrap gap-2">
              {meetingParticipants.map((p) => {
                const isSecretary = String(p.id) === String(activeMeeting?.created_by);
                const isConnected = isParticipantConnected(p.id);
                const canTransfer = userIsSecretary && !isSecretary && isConnected && Boolean(onTransferSecretary);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => {
                      if (canTransfer) onTransferSecretary(p.id);
                    }}
                    disabled={!canTransfer}
                    title={canTransfer ? 'Transférer le rôle de secrétaire' : undefined}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${isConnected ? 'bg-white border-emerald-200 text-emerald-700 shadow-sm' : 'bg-slate-100 border-slate-200 text-slate-500'} ${canTransfer ? 'cursor-pointer hover:bg-indigo-50 hover:border-indigo-200' : 'cursor-default'}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    {p.name}
                    {isSecretary && (
                      <span className="ml-1 text-[9px] uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                        Secrétaire
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {userIsSecretary && onInviteParticipant && (
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  value={inviteEmployeeId}
                  onChange={(e) => setInviteEmployeeId(e.target.value)}
                  className="flex-1 min-w-[140px] border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-100 outline-none"
                  disabled={availableInvitees.length === 0}
                >
                  <option value="">
                    {availableInvitees.length === 0 ? 'Aucune personne à inviter' : 'Inviter une personne'}
                  </option>
                  {availableInvitees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    if (!inviteEmployeeId) return;
                    onInviteParticipant(Number(inviteEmployeeId));
                    setInviteEmployeeId('');
                  }}
                  className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={!inviteEmployeeId}
                >
                  Inviter
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className={sectionTitle}>Bloc-notes</h3>
                <span className="text-[10px] text-slate-400">partagé, chacun peut écrire</span>
              </div>
              <SharedNotes value={meetingNotes || ''} onChange={onNotesChange} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className={sectionTitle}>Points</h3>
                <span className="text-[10px] text-slate-400">{visibleMeetingPoints.length}</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newPointTitle}
                  onChange={(e) => setNewPointTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddPoint();
                  }}
                  className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-100 outline-none"
                  placeholder="Ajouter un point…"
                />
                <button
                  type="button"
                  onClick={handleAddPoint}
                  className="px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={!newPointTitle.trim()}
                >
                  Ajouter
                </button>
              </div>
              {visibleMeetingPoints.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Aucun point pour le moment.</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {visibleMeetingPoints.map((point) => {
                    const isSelected = String(selectedMeetingPointId) === String(point.id);
                    const isDone = ['treated', 'converted'].includes((point.status || '').toLowerCase());
                    return (
                      <div
                        key={point.id}
                        onClick={() => userIsSecretary && onSelectMeetingPoint && onSelectMeetingPoint(point.id)}
                        className={`p-2 rounded-lg border text-sm transition-colors ${isSelected ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'} ${userIsSecretary ? 'cursor-pointer' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className={`font-medium ${isDone ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                            {point.title}
                          </div>
                          <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                            {pointStatusLabel(point.status)}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Par {point.author_name || 'un membre'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {userIsSecretary && selectedPoint && (selectedPoint.status || 'open').toLowerCase() === 'open' && (
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => onPointDecision && onPointDecision(selectedPoint.id, 'treated')}
                    className="flex-1 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded hover:bg-emerald-100"
                  >
                    Clore le point
                  </button>
                  <button
                    type="button"
                    onClick={() => onPointToTension && onPointToTension(selectedPoint)}
                    className="flex-1 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded hover:bg-indigo-100"
                  >
                    Transformer en tension
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernancePanel;
